/**
 * EntitlementSelector Component
 * Multi-select interface for choosing entitlements from the available entitlement catalog
 * Fetches entitlements dynamically from the API - no hardcoding
 */

import { useState, useMemo } from 'react';
import { useAdminEntitlementsList } from '../../hooks/useAdminEntitlementsList';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import type { EntitlementDefinition } from '../../hooks/useAdminEntitlementsList';

/** heapster-live's staff ids — never grantable by a redeem code. */
const STAFF_ENTITLEMENT_IDS = ['hlive:moderator', 'hlive:host', 'hlive:admin'];

function isOfferedOnRedeemGroup(id: string): boolean {
  return !id.startsWith('sys:') && !STAFF_ENTITLEMENT_IDS.includes(id);
}

interface EntitlementSelectorProps {
  selectedEntitlementIds: string[];
  onChange: (entitlementIds: string[]) => void;
  disabled?: boolean;
}

interface EntitlementCardProps {
  entitlement: EntitlementDefinition;
  isSelected: boolean;
  onToggle: (entitlementId: string) => void;
  disabled?: boolean;
}

function EntitlementCard({
  entitlement,
  isSelected,
  onToggle,
  disabled = false,
}: EntitlementCardProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle(entitlement.id);
    }
  };

  return (
    <div
      role="checkbox"
      aria-checked={isSelected}
      aria-label={`${entitlement.name}: ${entitlement.description}`}
      tabIndex={disabled ? -1 : 0}
      className={`p-3 rounded-lg border-2 cursor-pointer transition-colors ${
        isSelected
          ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20'
          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      onClick={() => !disabled && onToggle(entitlement.id)}
      onKeyDown={handleKeyDown}
    >
      <div className="flex items-center space-x-3">
        {/* Entitlement Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {entitlement.name}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {entitlement.id}
          </p>
        </div>

        {/* Selection Indicator */}
        <div className="flex-shrink-0">
          {isSelected ? (
            <svg
              className="w-5 h-5 text-sky-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          ) : (
            <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 rounded-full" />
          )}
        </div>
      </div>
    </div>
  );
}

export function EntitlementSelector({
  selectedEntitlementIds,
  onChange,
  disabled = false,
}: EntitlementSelectorProps) {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch all available entitlements - request a high limit to get all entitlements
  const { data, isLoading, error, isError } = useAdminEntitlementsList();

  // Only ids a redeem code may grant are offered: the backend refuses `sys:`
  // ids and heapster-live's staff ids on a redeem group
  // (`isRedeemableEntitlement` in functions/src/config/entitlements.ts, #2013),
  // so offering them would only produce a 400 on save.
  const entitlements = useMemo(
    () =>
      (data?.entitlements ?? []).filter((entitlement: EntitlementDefinition) =>
        isOfferedOnRedeemGroup(entitlement.id)
      ),
    [data]
  );

  // Filter entitlements based on search query
  const filteredEntitlements = useMemo(() => {
    if (!entitlements.length || !searchQuery) {
      return entitlements;
    }

    const query = searchQuery.toLowerCase();
    return entitlements.filter(
      (entitlement: EntitlementDefinition) =>
        entitlement.name.toLowerCase().includes(query) ||
        entitlement.id.toLowerCase().includes(query)
    );
  }, [entitlements, searchQuery]);

  const handleToggle = (entitlementId: string) => {
    if (disabled) return;

    const newSelection = selectedEntitlementIds.includes(entitlementId)
      ? selectedEntitlementIds.filter(id => id !== entitlementId)
      : [...selectedEntitlementIds, entitlementId];

    onChange(newSelection);
  };

  const handleSelectAll = () => {
    if (disabled || !filteredEntitlements.length) return;

    // Check if all filtered entitlements are selected
    const filteredEntitlementIds = filteredEntitlements.map(e => e.id);
    const allFilteredSelected = filteredEntitlementIds.every(id =>
      selectedEntitlementIds.includes(id)
    );

    if (allFilteredSelected) {
      // Deselect all filtered entitlements
      onChange(
        selectedEntitlementIds.filter(
          id => !filteredEntitlementIds.includes(id)
        )
      );
    } else {
      // Select all filtered entitlements (preserving existing selections)
      const newSelection = [
        ...selectedEntitlementIds.filter(
          id => !filteredEntitlementIds.includes(id)
        ),
        ...filteredEntitlementIds,
      ];
      onChange(newSelection);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-24 border border-gray-200 dark:border-gray-600 rounded-lg">
        <LoadingSpinner size="md" />
        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
          Loading entitlements...
        </span>
      </div>
    );
  }

  // Error state - fail fast, show clear error
  if (isError) {
    return (
      <div className="p-4 border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 rounded-lg">
        <p className="text-sm text-red-600 dark:text-red-400 font-medium">
          Failed to load entitlements
        </p>
        <p className="text-xs text-red-500 dark:text-red-400 mt-1">
          {error instanceof Error ? error.message : 'Unknown error occurred'}
        </p>
      </div>
    );
  }

  // Empty state - no entitlements exist
  if (entitlements.length === 0) {
    return (
      <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No entitlements available
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          Entitlements are defined in backend configuration
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Search Input */}
      <div className="mb-3">
        <input
          type="text"
          placeholder="Search entitlements..."
          aria-label="Search entitlements"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          disabled={disabled}
          className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 dark:bg-gray-800 dark:text-gray-100 disabled:opacity-50"
        />
      </div>

      {/* Empty Search Results */}
      {filteredEntitlements.length === 0 && searchQuery && (
        <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No matching entitlements
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            No entitlements match "{searchQuery}"
          </p>
        </div>
      )}

      {/* Selection Controls and Entitlement Grid - only show if we have filtered results */}
      {filteredEntitlements.length > 0 && (
        <>
          {/* Selection Controls */}
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {
                filteredEntitlements.filter(e =>
                  selectedEntitlementIds.includes(e.id)
                ).length
              }{' '}
              of {filteredEntitlements.length} selected
            </p>
            <button
              type="button"
              onClick={handleSelectAll}
              disabled={disabled}
              className="text-xs font-medium text-sky-600 hover:text-sky-500 dark:text-sky-400 dark:hover:text-sky-300 disabled:opacity-50"
            >
              {filteredEntitlements.every(e =>
                selectedEntitlementIds.includes(e.id)
              )
                ? 'Deselect All'
                : 'Select All'}
            </button>
          </div>

          {/* Entitlement Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
            {filteredEntitlements.map(entitlement => (
              <EntitlementCard
                key={entitlement.id}
                entitlement={entitlement}
                isSelected={selectedEntitlementIds.includes(entitlement.id)}
                onToggle={handleToggle}
                disabled={disabled}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
